import { useProfileImage } from '@/modules/WorkspaceSettings/useProfileImage'
import { useWorkspaceMembers } from '@/modules/WorkspaceSettings/useWorkspaceMembers'
import { computed, type Ref } from 'vue'
import type { Field } from './Fields/types'

/**
 * Provides extended extended information on tool field's corrected data.
 *
 * - whether it's been corrected
 * - by whom
 *
 * The naming of the composable is intentionally similar to
 * useFieldStatusIndicator to indicate we want to join them together.
 *
 * However, the implementation is completely separate, mainly because we're pulling user info
 * from a different pinia store.
 *
 * If user info were to be attached to the field at serialization,
 * we'd be able to simplify into a basic computed.
 */
export const useFieldStatusIndicatorExtended = (field: Ref<Field | undefined>) => {
  const workspaceMembersStore = useWorkspaceMembers()

  const isCorrectedModelOutput = computed(() => {
    return field.value?.manualValue !== null && field.value?.toolValueUpdatedBy !== null
  })

  const userProfileImage = useProfileImage(computed(() => field?.value?.manualValueUpdatedBy ?? ''))

  const lastCorrectedBy = computed(() => {
    const member = workspaceMembersStore.workspaceMembers.find(
      (i) => i.id === field?.value?.manualValueUpdatedBy,
    )
    if (!member) {
      return
    }

    return {
      name:
        member.firstName && member.lastName
          ? `${member.firstName} ${member.lastName}`
          : member.email,
      id: field.value?.manualValueUpdatedBy,
      avatarUrl: userProfileImage.value,
    }
  })

  return {
    isCorrectedModelOutput,
    lastCorrectedBy,
  }
}
